import { motion } from 'framer-motion';
import { Package, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const stockData = [
  {
    game: 'Pet Simulator 99',
    items: ['Titanics', 'Gargantuans', 'Huges', 'Gems'],
  },
  {
    game: 'Adopt Me',
    items: ['High Tiers', 'Mid Tiers', 'Low Tiers', 'Pet Wear'],
  },
  {
    game: 'Murder Mystery 2',
    items: ['Godlies', 'Ancients', 'Chromas'],
  },
];

const StockSection = ({ onDiscordClick }) => {
  return (
    <section className="relative py-24 px-4 bg-black" data-testid="stock-section">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
            <Package className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-semibold text-purple-400 uppercase tracking-wider">
              Currently In Stock
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700">
            What We Have In Stock
          </h2>
        </motion.div>

        {/* Stock cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {stockData.map((section, index) => (
            <motion.div
              key={section.game}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -8 }}
              className="group relative bg-gradient-to-br from-zinc-900 to-black border border-purple-900/30 rounded-2xl p-8 shadow-2xl hover:shadow-purple-500/20 transition-all duration-500"
              data-testid={`stock-card-${index}`}
            >
              {/* Top accent bar */}
              <div className="h-1 w-16 bg-gradient-to-r from-purple-600 to-purple-400 rounded-full mb-6" />

              <h3 className="text-2xl font-black text-white mb-6 group-hover:text-purple-400 transition-colors">
                {section.game}
              </h3>

              <ul className="space-y-3">
                {section.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-gray-300"
                  >
                    <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0" />
                    <span className={item === 'and More' ? 'italic text-purple-400' : ''}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Discord CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join the Discord server to see everything we have in stock!
          </p>
          <motion.div
            className="inline-block"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              onClick={onDiscordClick}
              size="lg"
              className="relative group bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-10 py-7 text-lg font-bold rounded-xl shadow-2xl shadow-purple-500/50 overflow-hidden transition-all duration-300"
              data-testid="stock-discord-button"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-purple-400 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                Join the Discord Server Here
              </span>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default StockSection;
